// What a membership covers, answered for one thing at a time.
//
// The booking flow asks this of every service, add-on, provider and date it
// draws, so it is pure and synchronous: everything it needs is already in the
// public plan payload (`serviceGroups`, `redemptionStaffIds`,
// `redemptionValidDays`) that the widget fetches once.
//
// Two audiences, and they are not the same question:
//
//   - Someone with no membership is being ADVERTISED to. Only plans sold online
//     qualify — a pill for a plan nobody can buy is a dead end.
//   - A member is being told what THEIR plan covers. Their own plans count
//     whether or not the spa still sells them, because they already own it.
//
// A member never sees another plan advertised on something their own plan does
// not cover: "Massage Club eligible" next to a service you thought you had is
// exactly the ambush the pills exist to prevent.

import type { MembershipPlanPublic } from './types';

/** Server day codes, indexed by JS `Date.getDay()`. */
const DAY_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export type Verdict =
  | { kind: 'none' }                                   // say nothing
  | { kind: 'advertise'; plans: MembershipPlanPublic[] }
  | { kind: 'covered'; plan: MembershipPlanPublic }
  | { kind: 'not-covered'; plan: MembershipPlanPublic };

export interface EligibilityInput {
  /** Plans sold online — the advertising pool. */
  plans: MembershipPlanPublic[];
  /** Ids of plans this client actually holds (active or cancelling). */
  ownedPlanIds: number[];
  /** Every plan the client holds, whether or not it is still sold online. */
  ownedPlans?: MembershipPlanPublic[];
}

const num = (v: unknown): number => Number(v);

/** Every service id a plan's groups cover. */
export function planServiceIds(plan: MembershipPlanPublic): number[] {
  const out: number[] = [];
  for (const g of ((plan as any).serviceGroups || [])) {
    for (const id of (g.serviceIds || [])) out.push(num(id));
  }
  return out;
}

/** Add-on option ids a plan's credits can be spent on, or that ride along free. */
export function planEnhancementIds(plan: MembershipPlanPublic): number[] {
  const out: number[] = [];
  for (const g of ((plan as any).serviceGroups || [])) {
    for (const id of (g.enhancementOptionIds || [])) out.push(num(id));
    for (const id of (g.includedEnhancementOptionIds || [])) out.push(num(id));
  }
  return out;
}

export function planCoversService(plan: MembershipPlanPublic, serviceId: unknown): boolean {
  return planServiceIds(plan).includes(num(serviceId));
}

export function planCoversEnhancement(plan: MembershipPlanPublic, optionId: unknown): boolean {
  return planEnhancementIds(plan).includes(num(optionId));
}

/** Empty `redemptionStaffIds` means anyone — the plan has no provider limit. */
export function planCoversProvider(plan: MembershipPlanPublic, staffId: unknown): boolean {
  const ids = (plan.redemptionStaffIds || []).map(num);
  if (!ids.length) return true;
  return ids.includes(num(staffId));
}

/** Empty `redemptionValidDays` means every day. `date` is `YYYY-MM-DD`. */
export function planCoversDate(plan: MembershipPlanPublic, date: string): boolean {
  const days = (plan as any).redemptionValidDays || [];
  if (!days.length) return true;
  const parts = String(date).split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return true;   // unparseable: don't claim a limit
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return days.includes(DAY_CODES[d.getDay()]);
}

/** The plans a client holds, resolved to plan objects where we have them. */
function ownedOf(input: EligibilityInput): MembershipPlanPublic[] {
  if (input.ownedPlans?.length) return input.ownedPlans;
  const owned = new Set((input.ownedPlanIds || []).map(num));
  return (input.plans || []).filter(p => owned.has(num(p.id)));
}

/**
 * The verdict for one thing, given a coverage test.
 *
 * A member's own plan always wins the label. Only when they hold nothing does
 * the advertising pool speak.
 */
function verdict(
  input: EligibilityInput,
  covers: (plan: MembershipPlanPublic) => boolean,
): Verdict {
  const owned = ownedOf(input);
  if (owned.length) {
    const hit = owned.find(covers);
    if (hit) return { kind: 'covered', plan: hit };
    // Their plan doesn't cover this. Say so — and never advertise another plan
    // over the top of it, which is how "I thought I was covered" happens.
    return { kind: 'not-covered', plan: owned[0] };
  }
  const advertise = (input.plans || []).filter(covers);
  return advertise.length ? { kind: 'advertise', plans: advertise } : { kind: 'none' };
}

export function serviceVerdict(input: EligibilityInput, serviceId: unknown): Verdict {
  return verdict(input, p => planCoversService(p, serviceId));
}

export function enhancementVerdict(input: EligibilityInput, optionId: unknown): Verdict {
  return verdict(input, p => planCoversEnhancement(p, optionId));
}

/**
 * Providers are only worth labelling once a service is chosen: a plan that
 * doesn't cover the service says nothing about who may perform it, and a pill
 * on every provider of an uncovered service is pure noise.
 */
export function providerVerdict(
  input: EligibilityInput, serviceId: unknown, staffId: unknown,
): Verdict {
  if (staffId == null || String(staffId).startsWith('filter')) return { kind: 'none' };
  // Nothing on the table covers this service, so who performs it is not a
  // membership question — labelling every provider "not covered" here would be
  // noise on a service that was never included in the first place.
  const owned = ownedOf(input);
  const pool = owned.length ? owned : (input.plans || []);
  if (!pool.some(p => planCoversService(p, serviceId))) return { kind: 'none' };
  return verdict(input, p => planCoversService(p, serviceId) && planCoversProvider(p, staffId));
}

/** The plans that cover a service and limit which days it can be redeemed. */
export function dayLimitedPlans(
  input: EligibilityInput, serviceId: unknown,
): MembershipPlanPublic[] {
  const owned = ownedOf(input);
  const pool = owned.length ? owned : (input.plans || []);
  return pool.filter(p =>
    planCoversService(p, serviceId) && ((p as any).redemptionValidDays || []).length > 0);
}

/** "Mon, Tue, Wed" for a plan's redeemable days, in week order. */
export function dayLabel(plan: MembershipPlanPublic): string {
  const days: string[] = (plan as any).redemptionValidDays || [];
  const order = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const pretty: Record<string, string> = {
    MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun',
  };
  return order.filter(d => days.includes(d)).map(d => pretty[d]).join(', ');
}

// ── The filter ──────────────────────────────────────────────────────────────

/**
 * Does a plan still leave anything bookable once its own limits are applied?
 * The filter offers only plans that do — a chip that filters to nothing is a
 * dead end dressed up as a choice.
 */
export function planHasBookableServices(
  plan: MembershipPlanPublic, availableServiceIds: number[],
): boolean {
  const covered = new Set(planServiceIds(plan));
  return availableServiceIds.some(id => covered.has(num(id)));
}

/**
 * Why a filtered step came back empty, in the client's words. Returning a
 * reason is the difference between "this spa has no availability" and "this
 * membership redeems Mon–Thu with Kayla or Sarah".
 */
export function filterEmptyReason(plan: MembershipPlanPublic): string {
  const bits: string[] = [];
  const days = dayLabel(plan);
  if (days) bits.push(`redeems ${days}`);
  const names = plan.redemptionStaffNames || [];
  if (names.length === 1) bits.push(`with ${names[0]}`);
  else if (names.length > 1) bits.push(`with ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`);
  if (!bits.length) return `${plan.name} doesn't cover anything available here.`;
  return `${plan.name} ${bits.join(' ')}.`;
}

// ── The upsell ──────────────────────────────────────────────────────────────

function money(n: number): string {
  return `$${Number(n || 0).toFixed(2).replace(/\.00$/, '')}`;
}

/**
 * The plan worth offering someone who has just booked.
 *
 * The moment is the point: they have decided to spend the money, and the plan
 * costs less than what they are holding. Two triggers —
 *
 *   - a non-member who booked something a plan covers, and
 *   - a member who booked something a DIFFERENT plan covers, which is either an
 *     upsell or a very plain signal that what they picked isn't in theirs.
 *
 * Nothing is offered when their own plan already covers it: they came to use
 * it, and selling someone the thing they own reads as not knowing who they are.
 */
export function upsellPlanFor(
  input: EligibilityInput, serviceId: unknown, staffId?: unknown,
): MembershipPlanPublic | null {
  const owned = new Set((input.ownedPlanIds || []).map(num));
  const covers = (p: MembershipPlanPublic) =>
    planCoversService(p, serviceId)
    && (staffId == null || planCoversProvider(p, staffId));

  if (ownedOf(input).some(covers)) return null;

  // Only plans actually on sale: an offer they cannot accept is worse than
  // silence.
  const candidates = (input.plans || []).filter(p => !owned.has(num(p.id)) && covers(p));
  if (!candidates.length) return null;
  // The cheapest way in — the offer should be the easiest yes available.
  return candidates.slice().sort((a, b) => Number(a.price) - Number(b.price))[0];
}

/**
 * What the offer may honestly claim. Credits vest going forward, so joining now
 * does NOT cover the visit just booked — saying otherwise is the sort of
 * promise a client discovers at the desk.
 */
export function upsellLine(plan: MembershipPlanPublic): string {
  const t: any = (plan as any).signupTerms;
  const per = plan.billingInterval === 1
    ? plan.billingUnit : `${plan.billingInterval} ${plan.billingUnit}s`;
  const price = t && t.periods > 1
    ? `${money(t.dueToday)} today, then ${money(t.perPeriod)}/${per}`
    : `${money(Number(plan.price))}/${per}`;
  return `${plan.name} — ${price}. Covers your next visit, not this one.`;
}
